import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  User, Mail, Phone, Briefcase, Shield, Globe, Check, Calendar,
  CheckCircle, Bell, Building2, TrendingUp, Award, Euro,
} from 'lucide-react';
import { AVATARS, getAvatarUrl, DEFAULT_AVATAR_ID } from '../config/avatars';
import { SUPPORTED_LANGUAGES, persistLanguage, readStoredLanguage } from '../i18n';
import { crmFetch } from '../components/crm/crmApi';
import { fmtMoney } from '../components/crm/constants';

const API = process.env.REACT_APP_BACKEND_URL;

const NOTIF_PREFS = [
  { key: 'taskReminders', label: 'Task & follow-up reminders', default: true },
  { key: 'overdueAlerts', label: 'Overdue task alerts', default: true },
  { key: 'placementUpdates', label: 'Placement & invoice status changes', default: true },
  { key: 'commissionUpdates', label: 'Commission earned notifications', default: true },
  { key: 'weeklySummary', label: 'Weekly performance summary', default: false },
];

const SnapshotStat = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-3">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0">
      <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
      <p className="text-[11px] text-gray-500">{label}</p>
    </div>
  </div>
);

export default function SalesSettings() {
  const { userProfile, session, refreshProfile } = useAuth();

  const [profileData, setProfileData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [selectedAvatar, setSelectedAvatar] = useState(DEFAULT_AVATAR_ID);
  const [avatarTab, setAvatarTab] = useState('animal');
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentLang = (readStoredLanguage() || 'en').split('-')[0];
  const [langOpen, setLangOpen] = useState(false);
  const [langShowAll, setLangShowAll] = useState(false);
  const [, forceLang] = useState(0);

  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarProvider, setCalendarProvider] = useState(null);
  const [calendarEmail, setCalendarEmail] = useState(null);
  const [calendarConnecting, setCalendarConnecting] = useState(false);

  const [notifs, setNotifs] = useState(() => Object.fromEntries(NOTIF_PREFS.map((n) => [n.key, n.default])));
  const [snapshot, setSnapshot] = useState(null);

  const isAdmin = userProfile?.role === 'jobasti_admin';
  const roleLabel = isAdmin ? 'Jobasti Admin' : 'Sales Executive';

  useEffect(() => {
    if (!userProfile) return;
    setSelectedAvatar(userProfile.avatarUrl || userProfile.avatar_url || userProfile.avatar_id || DEFAULT_AVATAR_ID);
    setProfileData({
      firstName: userProfile.firstName || userProfile.first_name || '',
      lastName: userProfile.lastName || userProfile.last_name || '',
      email: userProfile.email || '',
      phone: userProfile.phone || '',
    });
    const a = AVATARS.find((x) => x.id === (userProfile.avatar_id || userProfile.avatarUrl));
    if (a?.category) setAvatarTab(a.category);
  }, [userProfile]);

  const loadCalendar = useCallback(async () => {
    if (!userProfile?.id) return;
    try {
      const r = await axios.get(`${API}/api/calendar/status`, { params: { userId: userProfile.id } });
      setCalendarConnected(r.data?.connected || false);
      setCalendarProvider(r.data?.provider || null);
      setCalendarEmail(r.data?.email || null);
    } catch { setCalendarConnected(false); }
  }, [userProfile?.id]);

  const loadSnapshot = useCallback(async () => {
    const res = await crmFetch('/dashboard');
    if (res.success && res.data) setSnapshot(res.data);
  }, []);

  useEffect(() => { loadCalendar(); loadSnapshot(); }, [loadCalendar, loadSnapshot]);

  const saveProfile = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/api/profile/me`, {
        firstName: profileData.firstName, lastName: profileData.lastName, phone: profileData.phone,
      }, { headers: { Authorization: `Bearer ${session?.access_token}` } });
      await refreshProfile?.();
      toast.success('Profile updated');
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const saveAvatar = async (avatarId) => {
    try {
      setSavingAvatar(true);
      setSelectedAvatar(avatarId);
      await axios.put(`${API}/api/profile/avatar`, { avatar_id: avatarId }, { headers: { Authorization: `Bearer ${session?.access_token}` } });
      await refreshProfile?.();
    } catch { toast.error('Failed to save avatar'); }
    finally { setSavingAvatar(false); }
  };

  const changeLanguage = (code) => {
    if (!code) { setLangOpen(false); return; }
    persistLanguage(code);
    forceLang((n) => n + 1);
    if (session?.access_token) {
      axios.put(`${API}/api/profile/preferred-language`, { language: code }, { headers: { Authorization: `Bearer ${session.access_token}` } }).catch(() => {});
    }
    setLangOpen(false);
    toast.success('Language updated');
  };

  const connectCalendar = async (provider) => {
    try {
      setCalendarConnecting(true);
      const r = await axios.get(`${API}/api/calendar/auth-url`, { params: { userId: userProfile.id, provider } });
      if (r.data?.authUrl) window.location.href = r.data.authUrl;
      else toast.error(`${provider === 'outlook' ? 'Outlook' : 'Google'} calendar is not configured.`);
    } catch (e) { toast.error(e?.response?.data?.message || 'Calendar integration is not available yet.'); }
    finally { setCalendarConnecting(false); }
  };

  const disconnectCalendar = async () => {
    if (!window.confirm('Disconnect your calendar?')) return;
    try {
      await axios.post(`${API}/api/calendar/disconnect`, { userId: userProfile.id });
      setCalendarConnected(false);
      toast.success('Calendar disconnected');
    } catch { toast.error('Failed to disconnect'); }
  };

  const toggleNotif = (key) => {
    setNotifs((n) => ({ ...n, [key]: !n[key] }));
    toast.success('Preference saved');
  };

  return (
    <SalesLayout>
      <div data-testid="sales-settings-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your profile, calendar and notification preferences.</p>
        </div>

        <div className="space-y-6 max-w-4xl">
          {/* My Sales Snapshot */}
          <Card data-testid="settings-snapshot-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" /> My Sales Snapshot</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SnapshotStat icon={Building2} label="My Accounts" value={snapshot?.kpis?.activeAccounts ?? '—'} tone="bg-slate-100 text-slate-700" />
                <SnapshotStat icon={Briefcase} label="Open Jobs" value={snapshot?.kpis?.activeJobs ?? '—'} tone="bg-violet-100 text-violet-700" />
                <SnapshotStat icon={Award} label="Placements YTD" value={snapshot?.commission?.placementsYtd ?? '—'} tone="bg-emerald-100 text-emerald-700" />
                <SnapshotStat icon={Euro} label="Commission Earned" value={snapshot?.commission ? (fmtMoney(snapshot.commission.earnedYtd ?? 0) || '€0') : '—'} tone="bg-blue-100 text-blue-700" />
              </div>
            </CardContent>
          </Card>

          {/* Profile */}
          <Card data-testid="settings-profile-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4 text-blue-600" /> My Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6 mb-6">
                <img src={getAvatarUrl(selectedAvatar) || getAvatarUrl(DEFAULT_AVATAR_ID)} alt="Avatar" className="w-20 h-20 rounded-full object-cover ring-2 ring-blue-200" data-testid="settings-current-avatar" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{profileData.firstName} {profileData.lastName}</h3>
                  <p className="text-gray-600 text-sm">{profileData.email}</p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-600 font-medium">{roleLabel}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="settings-avatar-picker">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">Choose your avatar</p>
                  <div className="inline-flex bg-white border border-gray-200 rounded-lg p-0.5 text-xs">
                    {['animal', 'human'].map((cat) => (
                      <button key={cat} type="button" onClick={() => setAvatarTab(cat)}
                        className={`px-3 py-1.5 rounded-md font-medium transition capitalize ${avatarTab === cat ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                        data-testid={`settings-avatar-tab-${cat}`}>
                        {cat === 'animal' ? 'Animals' : 'People'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {AVATARS.filter((a) => a.category === avatarTab).map((avatar) => (
                    <button key={avatar.id} onClick={() => saveAvatar(avatar.id)} disabled={savingAvatar}
                      className={`w-14 h-14 rounded-full overflow-hidden transition-all ${selectedAvatar === avatar.id ? 'ring-3 ring-blue-500 ring-offset-2 scale-110' : 'ring-1 ring-gray-200 hover:ring-blue-300 hover:scale-105'}`}
                      title={avatar.label} data-testid={`settings-avatar-${avatar.id}`}>
                      <img src={avatar.url} alt={avatar.label} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">First Name</Label>
                  <Input value={profileData.firstName} onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })} data-testid="settings-firstname" />
                </div>
                <div>
                  <Label className="mb-1 block">Last Name</Label>
                  <Input value={profileData.lastName} onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })} data-testid="settings-lastname" />
                </div>
                <div>
                  <Label className="mb-1 block">Email</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <Input value={profileData.email} disabled className="bg-gray-50 text-gray-500" />
                  </div>
                </div>
                <div>
                  <Label className="mb-1 block">Phone</Label>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <Input value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} placeholder="+49 …" data-testid="settings-phone" />
                  </div>
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <Button onClick={saveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-profile-btn">
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Language */}
          <Card data-testid="settings-language-card" translate="no">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4 text-blue-600" /> Language</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative inline-block">
                <button type="button" onClick={() => setLangOpen((v) => !v)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:border-blue-400 transition text-sm font-medium text-gray-900 shadow-sm"
                  data-testid="settings-language-trigger">
                  <span className="text-lg leading-none">{SUPPORTED_LANGUAGES.find((l) => l.code === currentLang)?.flag || '🌐'}</span>
                  <span>{SUPPORTED_LANGUAGES.find((l) => l.code === currentLang)?.label || 'English'}</span>
                  <span className="text-gray-400 text-xs ml-1">▾</span>
                </button>
                {langOpen && (
                  <div className="absolute z-50 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden" data-testid="settings-language-menu">
                    <ul className="max-h-72 overflow-y-auto py-1">
                      {(langShowAll ? SUPPORTED_LANGUAGES : SUPPORTED_LANGUAGES.slice(0, 4)).map((lang) => (
                        <li key={lang.code}>
                          <button type="button" onClick={() => changeLanguage(lang.code)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-blue-50 transition ${lang.code === currentLang ? 'bg-blue-50/60 text-blue-700 font-semibold' : 'text-gray-800'}`}
                            data-testid={`settings-language-${lang.code}`}>
                            <span className="text-lg leading-none">{lang.flag}</span>
                            <span className="flex-1">{lang.label}</span>
                            {lang.code === currentLang && <Check className="w-4 h-4 text-blue-600" />}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-gray-100 px-3 py-2">
                      <button type="button" onClick={() => setLangShowAll((v) => !v)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                        {langShowAll ? 'Show less' : 'More languages'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Calendar */}
          <Card data-testid="settings-calendar-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-600" /> Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              {calendarConnected ? (
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">{calendarProvider === 'outlook' ? 'Outlook Calendar' : 'Google Calendar'}</h4>
                      <p className="text-xs text-gray-500">{calendarEmail ? `Connected as ${calendarEmail}` : 'Connected'}</p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={disconnectCalendar} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="settings-disconnect-calendar-btn">Disconnect</Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="settings-calendar-providers">
                  {[
                    { id: 'google', name: 'Google Calendar', img: 'https://www.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png', desc: 'Sync client meetings with Google' },
                    { id: 'outlook', name: 'Outlook Calendar', img: 'https://img.icons8.com/color/48/microsoft-outlook-2019.png', desc: 'Sync client meetings with Outlook' },
                  ].map((p) => (
                    <button key={p.id} onClick={() => connectCalendar(p.id)} disabled={calendarConnecting}
                      className="border border-gray-200 hover:border-blue-400 hover:bg-blue-50/40 rounded-lg p-4 text-left transition disabled:opacity-50"
                      data-testid={`settings-connect-${p.id}-btn`}>
                      <div className="flex items-center gap-3">
                        <img src={p.img} alt={p.name} className="w-8 h-8 object-contain shrink-0" />
                        <div className="min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm">{p.name}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2 font-medium">{calendarConnecting ? 'Connecting…' : 'Connect →'}</p>
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700"><strong>Tip:</strong> Connect a calendar to schedule and track client meetings straight from your CRM.</p>
              </div>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card data-testid="settings-notifications-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4 text-blue-600" /> Notifications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {NOTIF_PREFS.map((n) => (
                  <label key={n.key} className="flex items-center justify-between cursor-pointer" data-testid={`settings-notif-${n.key}`}>
                    <span className="text-sm text-gray-700">{n.label}</span>
                    <input type="checkbox" checked={!!notifs[n.key]} onChange={() => toggleNotif(n.key)} className="rounded text-blue-600 w-5 h-5" data-testid={`settings-notif-toggle-${n.key}`} />
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </SalesLayout>
  );
}
